import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ReactNode } from 'react'

const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 3) return false

  if (error && typeof error === 'object' && 'name' in error && error.name === 'OfflineError') {
    return false
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status
    if (status === 401) return true
    if (status >= 400 && status < 500) return false
  }

  return true
}

const getRetryDelay = (attemptIndex: number): number => {
  return Math.min(1000 * Math.pow(2, attemptIndex), 4000)
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: shouldRetry,
      retryDelay: getRetryDelay,
      networkMode: 'online',
    },
    mutations: {
      retry: shouldRetry,
      retryDelay: getRetryDelay,
      networkMode: 'online',
    },
  },
})

interface QueryProviderProps {
  children: ReactNode
}

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
